import {fetchAuthSession} from "aws-amplify/auth";
import axios, {AxiosError, AxiosRequestConfig} from "axios";


async function getAccessToken(forceRefresh: boolean = false): Promise<string | null> {
  try {
    const {accessToken} = (await fetchAuthSession({forceRefresh})).tokens ?? {};
    if(!accessToken || !accessToken?.toString()) return null;
    // Force refresh if the token is about to expire (5 mins)
    if(!forceRefresh && accessToken?.payload?.exp) {
      const expiration = accessToken.payload.exp * 1000;
      const now = (new Date).getTime();
      const fiveMinutes = 5 * 60 * 1000;
      if(expiration - now < fiveMinutes)
        return await getAccessToken(true);
    }
    return accessToken?.toString();
  } catch (error) {
    return null;
  }
}

// Redirect as POST to ID Server, with access token as parameter
export async function redirectAsPost(url: string, params: any = {}): Promise<void> {
  params.accessToken = await getAccessToken();
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;

  Object.keys(params).forEach(key => {
    const hiddenField = document.createElement("input");
    hiddenField.type = "hidden";
    hiddenField.name = key;
    hiddenField.value = params[key];
    form.appendChild(hiddenField);
  });

  document.body.appendChild(form);
  form.submit();
}


// Global cache for all API requests
// getCache and setCache are used only internally to the senRequest functions.
const cache: any = {};
const getCache = async(url: string, method: string = "", param: any = "", cacheExpiration: number = 2000) => {
  const key = `${url}(${method}){${JSON.stringify(param)}}`;
  const cacheData = cache[key];
  // If the method is not get, we use this to prevent spamming
  if(method !== "get") cacheExpiration = cacheExpiration < 500 ? cacheExpiration : 500;
  if(cacheData && (new Date).getTime() - cacheData.timestamp < cacheExpiration)
    return (await cacheData.response)?.data;
  return undefined;
};
const setCache = (url: string, method: string = "", param: any = "", response: any) => {
  const key = `${url}(${method}){${JSON.stringify(param)}}`;
  cache[key] = {
    response,
    timestamp: (new Date).getTime()
  };
};


export interface AuthRequestProps {
  cacheExpiration?: number;
  url: string;
  method?: "get" | "post" | "put" | "delete";
  params?: Record<string, any>;
  accessToken?: string,
  refreshFails?: number,
  forceRefresh?: boolean,
  files?: File[]
}
export async function sendAuthenticatedRequest<T>({
  cacheExpiration,
  url,
  method,
  params = {},
  files = []
}: AuthRequestProps): Promise<T> {
  const cacheData = await getCache(url, method, params, cacheExpiration);
  if(cacheData !== undefined) return cacheData;


  const config: AxiosRequestConfig = {
    params: method === "get" ? params : undefined
  };
  config.headers= {};

  let data = params;

  if(files && files.length) {
    data = new FormData;
    Object.entries(params).forEach(([key, value]) => {
      data.append(key, value);
    });
    config.headers["Content-Type"] = "multipart/form-data";
    files.forEach(file => {
      data.append("files", file);
    });
  } else
    config.headers["Content-Type"] = "application/json";

  const accessToken = await getAccessToken();
  if(accessToken)
    config.headers.Authorization = `Bearer ${accessToken}`;


  try {
    const apiCall = axios({
      data,
      method,
      url,
      ...config
    });
    setCache(url, method, params, apiCall);
    const response = await apiCall;
    return response.data;
  } catch (error: unknown) {
    const status = (error as AxiosError).response?.status || 400;
    const {code} = (error as AxiosError);

    console.error("Connection error", code, status);
    throw (error as AxiosError).response?.data || error;
  }
}
